import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { verifyAPI } from '../services/api';

const STATUS_CONFIG = {
  ACTIVE: {
    bg:     'bg-green-50',
    border: 'border-green-400',
    badge:  'bg-green-100 text-green-800',
    icon:   '✅',
    ar:     'هذه التذكرة صالحة',
    en:     'TICKET VALID',
    color:  'text-green-700',
    dot:    'bg-green-500',
  },
  USED: {
    bg:     'bg-red-50',
    border: 'border-red-400',
    badge:  'bg-red-100 text-red-800',
    icon:   '🔴',
    ar:     'هذه التذكرة مستخدمة',
    en:     'TICKET USED',
    color:  'text-red-700',
    dot:    'bg-red-500',
  },
  CANCELLED: {
    bg:     'bg-orange-50',
    border: 'border-orange-400',
    badge:  'bg-orange-100 text-orange-800',
    icon:   '⚠️',
    ar:     'هذه التذكرة ملغاة',
    en:     'TICKET CANCELLED',
    color:  'text-orange-700',
    dot:    'bg-orange-400',
  },
  NOT_FOUND: {
    bg:     'bg-gray-50',
    border: 'border-gray-300',
    badge:  'bg-gray-100 text-gray-600',
    icon:   '❌',
    ar:     'تذكرة غير موجودة',
    en:     'TICKET NOT FOUND',
    color:  'text-gray-600',
    dot:    'bg-gray-400',
  },
  INVALID: {
    bg:     'bg-gray-50',
    border: 'border-gray-300',
    badge:  'bg-gray-100 text-gray-600',
    icon:   '❓',
    ar:     'تذكرة غير صالحة',
    en:     'INVALID TICKET',
    color:  'text-gray-600',
    dot:    'bg-gray-400',
  },
};

const VerifyPage = () => {
  const { bookingNumber } = useParams();
  const [searchParams]                = useSearchParams();
  const [result, setResult]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [phoneSuffix, setPhoneSuffix] = useState('');
  const [phoneResult, setPhoneResult] = useState(null); // true | false | null

  useEffect(() => {
    // sig comes from the signed QR/link (see backend utils/qrSignature.js) —
    // without it the ticket can't be authenticated, so nothing beyond
    // "invalid" is ever shown.
    verifyAPI.check(bookingNumber, searchParams.get('sig'))
      .then((res) => setResult(res.data))
      .catch(() => setResult({ success: false, status: 'NOT_FOUND', data: null }))
      .finally(() => setLoading(false));
  }, [bookingNumber, searchParams]);

  const checkPhone = () => {
    if (!result?.data?.phoneLast4) return;
    setPhoneResult(phoneSuffix.trim() === result.data.phoneLast4);
  };

  const cfg = STATUS_CONFIG[result?.status] || STATUS_CONFIG.INVALID;

  return (
    <div dir="rtl" className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="max-w-md mx-auto space-y-4">

        {/* Header */}
        <div className="text-center">
          <img src="/logo.png" alt="سافر" className="w-14 h-14 object-contain mx-auto mb-2"
            onError={(e) => { e.target.style.display = 'none'; }} />
          <h1 className="text-lg font-bold text-gray-800">التحقق من التذكرة</h1>
          <p className="text-xs text-gray-400 font-mono mt-0.5">{bookingNumber}</p>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl shadow p-10 text-center">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">جاري التحقق...</p>
          </div>
        ) : (
          <>
            {/* ── Status Card ── */}
            <div className={`rounded-2xl border-2 ${cfg.bg} ${cfg.border} p-6 text-center shadow`}>
              <div className="text-5xl mb-3">{cfg.icon}</div>
              <h2 className={`text-2xl font-bold mb-2 ${cfg.color}`}>{cfg.ar}</h2>
              <div className="flex items-center justify-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} ${result?.status === 'ACTIVE' ? 'animate-pulse' : ''}`} />
                <span className={`text-sm font-mono font-bold px-3 py-1 rounded-full ${cfg.badge}`}>
                  {result?.status || 'INVALID'}
                </span>
              </div>
              {result?.status === 'USED' && result?.data?.boardedAt && (
                <p className="text-xs text-red-500 mt-2">
                  استُخدمت في: {format(new Date(result.data.boardedAt), 'yyyy/MM/dd HH:mm')}
                </p>
              )}
            </div>

            {/* ── Ticket Details ── */}
            {result?.data && (
              <div className="bg-white rounded-2xl shadow p-5 space-y-3">
                <h3 className="text-sm font-bold text-gray-600 border-b pb-2">تفاصيل التذكرة</h3>

                <Row label="المسافر"   value={result.data.passengerName} />
                <Row label="الرحلة"
                  value={`${result.data.from} ← ${result.data.to}`}
                  mono={false}
                />
                <Row label="المقاعد"
                  value={result.data.seats?.join('، ') || '—'}
                />
                <Row label="موعد المغادرة"
                  value={result.data.departure
                    ? format(new Date(result.data.departure), 'yyyy/MM/dd HH:mm')
                    : '—'}
                />
                <Row label="الشركة"   value={result.data.companyName} />
                <Row label="رقم الحجز" value={result.data.bookingNumber} mono />
              </div>
            )}

            {/* ── Phone Verification (Phase 4) ── */}
            {result?.data?.phoneLast4 && (
              <div className="bg-white rounded-2xl shadow p-5">
                <h3 className="text-sm font-bold text-gray-600 mb-1">التحقق من رقم الهاتف</h3>
                <p className="text-xs text-gray-400 mb-3">
                  اطلب من صاحب التذكرة آخر 4 أرقام من رقم هاتفه وأدخلها للتأكد
                </p>
                <div className="flex gap-2">
                  <input
                    type="tel"
                    maxLength={4}
                    value={phoneSuffix}
                    onChange={(e) => { setPhoneSuffix(e.target.value.replace(/\D/g, '')); setPhoneResult(null); }}
                    placeholder="آخر 4 أرقام"
                    className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-center text-lg font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-blue-400"
                    dir="ltr"
                  />
                  <button
                    onClick={checkPhone}
                    disabled={phoneSuffix.length !== 4}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition"
                  >
                    تحقق
                  </button>
                </div>
                {phoneResult === true && (
                  <div className="mt-3 flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-4 py-2.5">
                    <span className="text-green-600 text-lg">✅</span>
                    <span className="text-green-700 text-sm font-semibold">رقم الهاتف صحيح — التذكرة تخص هذا الشخص</span>
                  </div>
                )}
                {phoneResult === false && (
                  <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                    <span className="text-red-600 text-lg">❌</span>
                    <span className="text-red-700 text-sm font-semibold">رقم الهاتف غير صحيح — قد تكون التذكرة مزورة</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Anti-fraud Warning ── */}
            <div className="bg-amber-50 border border-amber-300 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <span className="text-2xl flex-shrink-0">⚠️</span>
                <div>
                  <p className="text-amber-900 font-bold text-sm">تحذير من التزوير</p>
                  <p className="text-amber-800 text-xs mt-1 leading-relaxed">
                    لا تقبل أو تدفع ثمن هذه التذكرة إلا إذا كانت حالتها <strong>صالحة (ACTIVE)</strong> ورقم الهاتف مطابق.
                    التذكرة المستخدمة أو الملغاة لا قيمة لها.
                  </p>
                </div>
              </div>
            </div>

            {/* ── Verified by ── */}
            <div className="text-center text-xs text-gray-400 pb-4">
              <p>تم التحقق بواسطة منصة سافر للنقل</p>
              <p className="font-mono mt-0.5">{new Date().toLocaleString('ar-SA')}</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value, mono = false }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-xs text-gray-400 flex-shrink-0">{label}</span>
    <span className={`text-sm font-semibold text-gray-800 text-left truncate ${mono ? 'font-mono' : ''}`} dir="ltr">
      {value || '—'}
    </span>
  </div>
);

export default VerifyPage;
